/**
 * PreviewCard (HoverPreviewCard) - Enlarged preview shown on hover,
 * displaying avatar image and full RPG-style attribute bars.
 */

import { useState } from 'react';
import { buildApiUrl } from '@/services/urlResolver';
import { isLocalMode, isElectronMode } from '@/utils/apiMode';
import type { LLMModelOption } from '@/types';
import { getModelAttributes } from '@/constants/llm/modelAttributes';
import { getAvatarForModel } from './modelAvatars';
import { PROVIDER_CONFIG } from './providerConfig';
import { AttributeBar } from './AttributeBar';

// Avatar API endpoint
const AVATAR_API_ENDPOINT = '/api/v1/llm/avatars';

export function HoverPreviewCard({
  model,
  modelName,
  position,
}: {
  model: LLMModelOption;
  modelName: string;
  position: { x: number; y: number };
}) {
  const modelId = model.value;
  const [isLoaded, setIsLoaded] = useState(false);
  // Subdirectory avatars only exist in cloud (GCS), use flat filenames locally
  const isLocal = isLocalMode() || isElectronMode();
  const avatarFile = getAvatarForModel(modelId, !isLocal);
  const avatarUrl = buildApiUrl(`${AVATAR_API_ENDPOINT}/${avatarFile}`);
  const attrs = getModelAttributes(model);

  const provider = modelId.split('/')[0];
  const providerConfig = PROVIDER_CONFIG[provider] || { name: provider, color: 'text-gray-400' };

  return (
    <div
      className="fixed z-50 pointer-events-none"
      style={{
        left: position.x,
        top: position.y,
        transform: 'translate(-50%, -110%)',
      }}
    >
      <div className="bg-black/80 backdrop-blur-xl rounded-xl shadow-2xl border border-white/10 p-3 animate-in zoom-in-95 duration-150 w-64">
        {/* Avatar Image */}
        <div className="relative w-full h-48 rounded-lg overflow-hidden bg-white/5 mb-3">
          {!isLoaded && (
            <div className="overlay-center">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          <img
            src={avatarUrl}
            alt={modelName}
            className={`w-full h-full object-cover transition-opacity duration-200 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setIsLoaded(true)}
          />
        </div>

        {/* Model Name */}
        <p className="text-center text-sm font-semibold text-white truncate">{modelName}</p>
        <p className={`text-center text-xs ${providerConfig.color} mb-3`}>{providerConfig.name}</p>

        {/* Stats Bars */}
        {attrs && (
          <div className="space-y-1.5">
            <AttributeBar abbrev="SPD" score={attrs.speed} />
            <AttributeBar abbrev="AGI" score={attrs.agility} />
            <AttributeBar abbrev="INT" score={attrs.intelligence} />
            <AttributeBar abbrev="PWR" score={attrs.power} />
            <AttributeBar abbrev="ATK" score={attrs.attack} />
            <AttributeBar abbrev="DEF" score={attrs.defense} />
          </div>
        )}
      </div>
    </div>
  );
}

export default HoverPreviewCard;
